import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';

import { Fai, ProductFactory, PromotionModel, PromotionsService } from '@bytel/bytel-sales';
import { Oauth2Service } from '@bytel/checkout';

import { AppService } from '@services/app.services';
import { ConfigurationService } from '@services/configuration.service';
import { FaiService } from '@services/fai.services';
import { Ftth3ClicsService } from '@services/ftth3-clics.service';
import { MainCartService } from '@services/main-cart.service';

import { BasicObject } from '@interfaces/checkout.interface';

import { DataCyDirective } from '../../../../common/directives/data-cy.directive';
import { DataCsMaskDirective } from '../../../../common/directives/data-mask.directive';
import { EquipmentLabelPipe } from '../../../../shared/pipes/equipment-label.pipe';
import { ObligationPipe } from '../../../../shared/pipes/obligation.pipe';
import { PlaysLabelPipe } from '../../../../shared/pipes/plays-label.pipe';
import { PriceInitialPipe } from '../../../../shared/pipes/price-initial.pipe';
import { SmartTVPipe } from '../../../../shared/pipes/smart-tv.pipe';
import { PriceComponent } from '../../../../trilogy/price/price.component';
import { PromoHomeService } from '../promo-home.service';
import { DebitPlusBlockComponent } from './debit-plus-block/debit-plus-block.component';

@Component({
    selector: 'checkout-fai',
    templateUrl: './fai.component.html',
    styleUrl: 'fai.component.scss',
    standalone: true,
    imports: [
        PriceComponent,
        DataCsMaskDirective,
        DataCyDirective,
        PlaysLabelPipe,
        SmartTVPipe,
        PriceInitialPipe,
        CommonModule,
        EquipmentLabelPipe,
        ObligationPipe,
        DebitPlusBlockComponent,
    ],
})
export class FaiComponent implements OnInit {
    @Input() public quoteIndex: number = 0;
    @Input() subscription: Fai;

    public eligibility: BasicObject;
    public equipment: BasicObject = {};
    public images: string;
    public isFtth3Clics: boolean = false;
    public speedDownLabel: string;
    public speedUpLabel: string;
    public isFaimUnlimited: boolean = false;
    public userAddress: string;
    public veryHighSpeed: boolean = false;
    public dataEnvelope: string = '';
    public isTerebiOffer: boolean = false;
    public automaticPromos: PromotionModel[] = [];
    public promoHome: PromotionModel;
    public canHavePromoHome: boolean = false;
    public isConnected: boolean = false;
    public showHighSpeedOption: boolean = false;

    private oauth2Service: Oauth2Service = inject(Oauth2Service);
    private appService: AppService = inject(AppService);
    private cartService: MainCartService = inject(MainCartService);
    private configurationService: ConfigurationService = inject(ConfigurationService);
    private faiService: FaiService = inject(FaiService);
    private ftth3ClicsService: Ftth3ClicsService = inject(Ftth3ClicsService);
    private promoHomeService: PromoHomeService = inject(PromoHomeService);

    constructor() {
        this.eligibility = this.faiService.eligibility;
    }

    public ngOnInit(): void {
        this.isFtth3Clics = this.ftth3ClicsService.isActive();
        this.isFaimUnlimited = ProductFactory.Is(this.subscription, 'FaimUnlimited');
        if (!this.isFaimUnlimited) {
            this._setSpeed();
        }

        this.dataEnvelope = this.subscription.data.data_envelope;
        this._setUserAddress();
        this.isTerebiOffer = this.subscription.data.commercial_series === 'medium_tv_xgbox';
        const promos = PromotionsService.GetPromotionsForProduct(
            this.subscription,
            this.cartService.cartModel.promotions,
        );

        this.isConnected = this.oauth2Service.isConnected;

        this.automaticPromos = promos?.automatic;

        this.promoHome = this.promoHomeService.getApplicablePromoHome(
            this.subscription,
            this.cartService.cartModel.promotions,
            this.isConnected,
        );
        this.canHavePromoHome = !!this.promoHome;
        this.showHighSpeedOption = this.faiService.canSubscribeOptions(this.subscription);
    }

    public goToWall(): void {
        this.ftth3ClicsService.stopUsingFtth3Clics();
        this.appService.navigateTo(this.configurationService.getData()?.global.wall.fai_eligibility);
    }

    private _setSpeed(): void {
        const data = this.subscription.data;

        if (data.technology === Fai.TECHNOLOGIES.FTTH) {
            this.speedDownLabel = data.speed_down;
            this.speedUpLabel = data.speed_up;
            this.veryHighSpeed = true;
        } else {
            const speedDown = this.faiService.eligibility.xDSLtechnoSpeed.speedDown;
            const speedUp = this.faiService.eligibility.xDSLtechnoSpeed.speedUp;

            this.speedUpLabel = `${speedUp} Mb/s`;
            if (speedDown === 1000) {
                this.speedDownLabel = '1 Gb/s';
                this.veryHighSpeed = true;
            } else {
                this.speedDownLabel = `${speedDown} Mb/s`;
            }
        }
    }

    private _setUserAddress(): void {
        const address = this.faiService?.eligibility?.address;
        if (address) {
            this.userAddress = `${address.streetNumber.number} ${address.streetNumber.complement || ''} ${
                address.street.label
            } ${address.zipCode} ${address.city.label}`;
        }
    }
}
